import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { line, Color, SSO_STATE_STORAGE_KEY, Size, emptyLogin, lockoutCountdown, loginProblems, passwordProblems, t, type LoginDraft, type PasskeyRequestOptions, type RedeemOutcome, type SignInOutcome, type SsoStatus } from '@bms2/shared';
import { Button, Checkbox, PasswordInput, TextInput } from '../components/controls';
import { AlertBanner } from '../components/controls/AlertBanner';
import { PublicBand } from '../shell/PublicBand';
import { apiFetch } from './records/api';
import { readDeviceToken, syncSessionMarker, writeDeviceToken, writeSessionToken } from '../session';
import { getPasskey, passkeysSupported } from '../passkeys';
import styles from './LoginPage.module.css';

/**
 * The Login Screen (piece 14, figure 108): a dedicated page, never a modal,
 * because before a session exists there is no current page to open one over.
 * Email, password and the multi-factor code live on the one screen and submit
 * together, never split across pages. 🔨 There is no self-registration link
 * anywhere on it: accounts are created only through an invitation.
 *
 * Two states follow a sign-in and stay on this page: enrolling the
 * authenticator on a login with no factor yet, and setting a new password on
 * the default credential, which is the one thing that credential can do.
 *
 * Piece 21 adds the rest of a real login's life on the same page: the lockout
 * countdown that disables Sign In until the real window clears, the reset
 * request, an invitation link's `?token=` or a typed activation code redeemed
 * into the set-password screen, and the recovery codes shown once after
 * enrolment.
 */
export const SIGN_IN_PATH = '/auth/sign-in';
export const VERIFY_CODE_PATH = '/auth/verify-code';
/** The second step's other answer (decision 0351): the passkey's request options, and the assertion against the same challenge token. */
export const VERIFY_PASSKEY_OPTIONS_PATH = '/auth/verify-passkey/options';
export const VERIFY_PASSKEY_PATH = '/auth/verify-passkey';
export const ENROL_PATH = '/auth/enroll';
export const SET_PASSWORD_PATH = '/auth/set-password';
export const REDEEM_PATH = '/auth/redeem';
export const ACTIVATE_PATH = '/auth/activate';
export const RESET_REQUEST_PATH = '/auth/reset-request';
/**
 * Single sign-on (decision 0352): the status the button is drawn from, the
 * start that answers the authorization URL, and the callback the provider's
 * return is handed to. 🔨 The flow begins and ends on this page: the state
 * is kept in session storage and a return the browser did not start is
 * refused before the API is asked.
 */
export const SSO_STATUS_PATH = '/auth/sso/status';
export const SSO_START_PATH = '/auth/sso/start';
export const SSO_CALLBACK_PATH = '/auth/sso/callback';

function readSsoState(): string | null {
  try {
    return window.sessionStorage.getItem(SSO_STATE_STORAGE_KEY);
  } catch {
    // silent: a browser refusing storage forgets the choice, nothing more.
    return null;
  }
}

function writeSsoState(state: string | null): void {
  try {
    if (state === null) window.sessionStorage.removeItem(SSO_STATE_STORAGE_KEY);
    else window.sessionStorage.setItem(SSO_STATE_STORAGE_KEY, state);
  } catch {
    // silent: a browser refusing storage forgets the choice, nothing more
    // Storage refused: the return will be refused as one this browser did not start.
  }
}

export interface LoginPageProps {
  apiBaseUrl?: string;
  /** Where a signed-in person lands; the Dashboard unless the route says otherwise. */
  landing?: string;
}

type Stage =
  | { kind: 'form' }
  | { kind: 'enrol'; enrolmentToken: string; secret: string; otpauthUrl: string }
  | { kind: 'setPassword'; currentPassword: string }
  | { kind: 'forgot'; sent: boolean }
  | { kind: 'code' }
  /** The second step of a sign-in: the password held, the code is asked for on its own (decision 0325). */
  | { kind: 'factor'; challengeToken: string; trustedDeviceDays: number; /** The login holds a passkey, so Use a passkey stands beside the code (decision 0351). */ passkey: boolean }
  | { kind: 'activate'; activationToken: string; email: string }
  | { kind: 'recoveryCodes'; codes: readonly string[] };

async function postJson<T>(url: string, body: unknown): Promise<T | null> {
  const response = await apiFetch(url, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) }).catch(/* silent: a page open without a session posts no report (piece 9: a report is posted only by a signed-in person). */ () => null);
  if (!response) return null;
  return (await response.json().catch(/* silent: a page open without a session posts no report (piece 9: a report is posted only by a signed-in person). */ () => null)) as T | null;
}

export function LoginPage({ apiBaseUrl = '', landing = '/' }: LoginPageProps) {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  /**
   * A stale token never rides the sign-in (found while building, 2026-09-11), so the screen asks the server and drops a marker
   * it no longer honours. 🔒 Decision 3540 (T2313): a marker the server still honours stays, because opening the Login Screen
   * ends no session; it dropped every marker on opening until then, and Sign Out then left the cookie's session live.
   */
  useEffect(() => {
    void syncSessionMarker(apiBaseUrl, (url) => apiFetch(url, {}, { skipAuthRedirect: true }));
  }, [apiBaseUrl]);
  const [draft, setDraft] = useState<LoginDraft>(emptyLogin);
  /** The lockout's end as an instant, so the countdown reads the real window rather than a message. */
  const [lockedUntil, setLockedUntil] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [activationCode, setActivationCode] = useState('');
  const [attempted, setAttempted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [stage, setStage] = useState<Stage>({ kind: 'form' });
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const otpauthUrl = stage.kind === 'enrol' ? stage.otpauthUrl : null;
  useEffect(() => {
    if (!otpauthUrl) return undefined;
    let live = true;
    // A runtime without a canvas draws no code and the secret stands alone.
    // T643: the encoder is fetched only when a secret is being enrolled, not with the entry chunk.
    import('qrcode').then(({ default: QRCode }) => QRCode.toDataURL(otpauthUrl, { margin: 1, width: 192 })).then((url) => { if (live) setQrDataUrl(url); }).catch(/* silent: a runtime without a canvas draws no code, and the secret stands alone. */ () => undefined);
    return () => { live = false; };
  }, [otpauthUrl]);
  const [enrolCode, setEnrolCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  /** Whether the installation offers single sign-on, read once before a session exists (decision 0352). */
  const [sso, setSso] = useState<SsoStatus | null>(null);
  const [ssoWorking, setSsoWorking] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    apiFetch(`${apiBaseUrl}${SSO_STATUS_PATH}`, { signal: controller.signal })
      .then((response) => (response.ok ? (response.json() as Promise<SsoStatus>) : null))
      .then((body) => {
        if (!controller.signal.aborted && body) setSso(body);
      })
      .catch(/* silent: a page open without a session posts no report (piece 9: a report is posted only by a signed-in person). */ () => undefined);
    return () => controller.abort();
  }, [apiBaseUrl]);
  const locked = lockedUntil !== null && lockedUntil > now;
  const secondsLeft = locked ? Math.ceil((lockedUntil - now) / 1000) : 0;
  useEffect(() => {
    if (!locked) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [locked]);

  /** A person sent here by a refused request lands back on the page they asked for (piece 21). */
  const next = params.get('next');
  const destination = next && next.startsWith('/') && !next.startsWith('//') ? next : landing;
  /** An invitation or reset link lands here with `?token=`; it is redeemed once and the parameter is dropped from the address. */
  const token = params.get('token');
  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    postJson<RedeemOutcome>(`${apiBaseUrl}${REDEEM_PATH}`, { token }).then((outcome) => {
      if (cancelled) return;
      setParams({}, { replace: true });
      if (!outcome) setMessage(t('login.unreachable'));
      else if (outcome.kind === 'refused') setMessage(outcome.reason);
      else setStage({ kind: 'activate', activationToken: outcome.activationToken, email: outcome.email });
    }).catch(/* silent: a page open without a session posts no report (piece 9: a report is posted only by a signed-in person). */ () => undefined);
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, token, setParams]);
  const [newPasswordAgain, setNewPasswordAgain] = useState('');
  const problems = loginProblems(draft);
  const set = <K extends keyof LoginDraft>(field: K) => (value: LoginDraft[K]) => setDraft((d) => ({ ...d, [field]: value }));

  /** A session answer: the token is kept, and the default credential goes to set-password before anything else. */
  const settle = useCallback(
    (outcome: SignInOutcome | null, currentPassword: string) => {
      if (!outcome) {
        setMessage(t('login.unreachable'));
        return;
      }
      if (outcome.kind === 'refused') {
        setMessage(outcome.reason);
        return;
      }
      if (outcome.kind === 'locked') {
        // 🔨 The countdown runs from the real window the server named, and Sign In is disabled until it clears (piece 21).
        setLockedUntil(Date.now() + outcome.secondsLeft * 1000);
        setNow(Date.now());
        setMessage(null);
        return;
      }
      if (outcome.kind === 'enrol') {
        setMessage(null);
        setStage({ kind: 'enrol', enrolmentToken: outcome.enrolmentToken, secret: outcome.secret, otpauthUrl: outcome.otpauthUrl });
        return;
      }
      if (outcome.kind === 'challenge') {
        setMessage(null);
        setStage({ kind: 'factor', challengeToken: outcome.challengeToken, trustedDeviceDays: outcome.trustedDeviceDays, passkey: outcome.passkey === true });
        return;
      }
      writeSessionToken(outcome.token);
      // The device's proof, where the bearer transport carries it; under the cookie transport the browser already holds it (decision 0324).
      if (outcome.deviceToken) writeDeviceToken(outcome.deviceToken);
      if (outcome.mustChangePassword) {
        setMessage(null);
        setStage({ kind: 'setPassword', currentPassword });
        return;
      }
      if (outcome.recoveryCodes && outcome.recoveryCodes.length > 0) {
        setMessage(null);
        setStage({ kind: 'recoveryCodes', codes: outcome.recoveryCodes });
        return;
      }
      navigate(destination);
    },
    [destination, navigate],
  );

  /** The provider's return: `?code=` and `?state=` land here, the state is held to the one this browser kept, and the API finishes the sign-in. */
  const ssoCode = params.get('code');
  const ssoState = params.get('state');
  /** The state already handed to the API, so dropping the parameters from the address never cancels or repeats the one exchange. */
  const ssoHandled = useRef<string | null>(null);
  useEffect(() => {
    if (!ssoCode || !ssoState || ssoHandled.current === ssoState) return;
    ssoHandled.current = ssoState;
    setParams({}, { replace: true });
    const kept = readSsoState();
    writeSsoState(null);
    // The exchange runs off the render: a foreign state is refused before the API is asked, and the API's answer settles like the password's.
    Promise.resolve()
      .then(() => {
        if (kept !== ssoState) {
          setMessage(t('login.ssoStateMismatch'));
          return undefined;
        }
        setSsoWorking(true);
        return apiFetch(`${apiBaseUrl}${SSO_CALLBACK_PATH}?code=${encodeURIComponent(ssoCode)}&state=${encodeURIComponent(ssoState)}`)
          .then((response) => response.json().catch(/* silent: a page open without a session posts no report (piece 9: a report is posted only by a signed-in person). */ () => null) as Promise<SignInOutcome | null>)
          .catch(/* silent: a page open without a session posts no report (piece 9: a report is posted only by a signed-in person). */ () => null);
      })
      .then((outcome) => {
        if (outcome === undefined) return;
        setSsoWorking(false);
        settle(outcome, '');
      });
  }, [apiBaseUrl, ssoCode, ssoState, setParams, settle]);

  /** The button: the API answers the authorization URL and the state; the state is kept here and the browser follows the URL. */
  const startSso = async () => {
    setMessage(null);
    setSsoWorking(true);
    const response = await apiFetch(`${apiBaseUrl}${SSO_START_PATH}`).catch(/* silent: a page open without a session posts no report (piece 9: a report is posted only by a signed-in person). */ () => null);
    const body = response ? ((await response.json().catch(/* silent: a page open without a session posts no report (piece 9: a report is posted only by a signed-in person). */ () => null)) as { authorizationUrl?: string; state?: string; message?: string } | null) : null;
    if (!response?.ok || !body?.authorizationUrl || !body.state) {
      setSsoWorking(false);
      setMessage(body?.message ?? t('login.unreachable'));
      return;
    }
    writeSsoState(body.state);
    window.location.assign(body.authorizationUrl);
  };

  const signIn = async (event: FormEvent) => {
    event.preventDefault();
    setAttempted(true);
    if (problems.length > 0) return;
    setBusy(true);
    // 🔨 The first step carries the email and the password alone; the code is asked for on its own step (decision 0325).
    const outcome = await postJson<SignInOutcome>(`${apiBaseUrl}${SIGN_IN_PATH}`, { email: draft.email.trim(), password: draft.password, code: null, deviceToken: readDeviceToken() });
    setBusy(false);
    settle(outcome, draft.password);
  };

  const verifyCode = async (event: FormEvent) => {
    event.preventDefault();
    if (stage.kind !== 'factor') return;
    const code = draft.code.trim();
    if (!code) return;
    setBusy(true);
    const outcome = await postJson<SignInOutcome>(`${apiBaseUrl}${VERIFY_CODE_PATH}`, { challengeToken: stage.challengeToken, code, trustDevice: draft.trustDevice });
    setBusy(false);
    settle(outcome, draft.password);
  };

  /** 🔨 The passkey answers the challenge the password earned, never the first step (decision 0351): the options, the browser's gesture, then the assertion against the same token. */
  const signInWithPasskey = async () => {
    if (stage.kind !== 'factor') return;
    if (!passkeysSupported()) return setMessage(t('login.passkeyUnsupported'));
    setBusy(true);
    const options = await postJson<PasskeyRequestOptions & { message?: string }>(`${apiBaseUrl}${VERIFY_PASSKEY_OPTIONS_PATH}`, { challengeToken: stage.challengeToken });
    const credential = options && typeof options.challenge === 'string' ? await getPasskey(options) : null;
    if (!credential) {
      setBusy(false);
      return setMessage(t('login.passkeyFailed'));
    }
    const outcome = await postJson<SignInOutcome>(`${apiBaseUrl}${VERIFY_PASSKEY_PATH}`, { challengeToken: stage.challengeToken, credential, trustDevice: draft.trustDevice });
    setBusy(false);
    settle(outcome, draft.password);
  };

  const enrol = async (event: FormEvent) => {
    event.preventDefault();
    if (stage.kind !== 'enrol') return;
    setBusy(true);
    const outcome = await postJson<SignInOutcome>(`${apiBaseUrl}${ENROL_PATH}`, { enrolmentToken: stage.enrolmentToken, code: enrolCode.trim() });
    setBusy(false);
    settle(outcome, draft.password);
  };

  const requestReset = async (event: FormEvent) => {
    event.preventDefault();
    if (!draft.email.trim()) return;
    setBusy(true);
    await postJson<{ ok: true }>(`${apiBaseUrl}${RESET_REQUEST_PATH}`, { email: draft.email.trim() });
    setBusy(false);
    setStage({ kind: 'forgot', sent: true });
  };

  const redeemCode = async (event: FormEvent) => {
    event.preventDefault();
    if (!activationCode.trim()) return;
    setBusy(true);
    const outcome = await postJson<RedeemOutcome>(`${apiBaseUrl}${REDEEM_PATH}`, { token: activationCode.trim() });
    setBusy(false);
    if (!outcome) return setMessage(t('login.unreachable'));
    if (outcome.kind === 'refused') return setMessage(outcome.reason);
    setMessage(null);
    setStage({ kind: 'activate', activationToken: outcome.activationToken, email: outcome.email });
  };

  const activate = async (event: FormEvent) => {
    event.preventDefault();
    if (stage.kind !== 'activate' || passwordIssues.length > 0 || newPassword !== newPasswordAgain) return;
    setBusy(true);
    const outcome = await postJson<SignInOutcome>(`${apiBaseUrl}${ACTIVATE_PATH}`, { activationToken: stage.activationToken, newPassword });
    setBusy(false);
    setDraft((d) => ({ ...d, email: stage.email, password: newPassword }));
    settle(outcome, newPassword);
  };

  const passwordIssues = passwordProblems(newPassword);
  const mismatch = newPasswordAgain !== '' && newPasswordAgain !== newPassword;
  const changePassword = async (event: FormEvent) => {
    event.preventDefault();
    if (stage.kind !== 'setPassword' || passwordIssues.length > 0 || newPassword !== newPasswordAgain) return;
    setBusy(true);
    const answer = await apiFetch(`${apiBaseUrl}${SET_PASSWORD_PATH}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ currentPassword: stage.currentPassword, newPassword }),
    }).catch(/* silent: a page open without a session posts no report (piece 9: a report is posted only by a signed-in person). */ () => null);
    setBusy(false);
    if (!answer) {
      setMessage(t('login.unreachable'));
      return;
    }
    if (!answer.ok) {
      const body = (await answer.json().catch(/* silent: a page open without a session posts no report (piece 9: a report is posted only by a signed-in person). */ () => null)) as { message?: string } | null;
      setMessage(body?.message ?? t('login.unreachable'));
      return;
    }
    navigate(destination);
  };

  const urlLine = `${t('login.enrolUrl')}: `;
  const problemText = (field: string, key: 'login.problem.email' | 'login.problem.password' | 'login.problem.code') => (attempted && problems.includes(field) ? t(key) : null);

  return (
    <main className={styles.page} data-page="login">
      {/* 🔒 The band every page outside the shell opens on (decision 0690). */}
      <PublicBand />
      <section className={styles.card} aria-labelledby="login-title">
        {message ? <AlertBanner tone="error" message={message} action={{ label: t('modal.dismiss'), onAction: () => setMessage(null) }} /> : null}

        {stage.kind === 'form' ? (
          <form className={styles.form} onSubmit={(event) => void signIn(event)} data-login-stage="form" noValidate>
            <h1 id="login-title" className={styles.title}>{t('login.title')}</h1>
            {/* Sign in with the provider, above the email form and never a lookup that decides for the person (piece 21; decision 0352). */}
            {sso?.enabled ? (
              <div className={styles.sso} data-login-sso={sso.providerName}>
                <Button size={Size.Medium} color={Color.Navy} text={[`${t('login.ssoButton')} ${sso.providerName}`]} actions={[() => void startSso()]} disabled={busy || ssoWorking} />
                <p className={styles.sectionHeading} role="status">{ssoWorking ? t('login.ssoWorking') : t('login.ssoOr')}</p>
              </div>
            ) : null}
            <TextInput size={Size.Medium} label={t('login.email')} value={draft.email} onChange={set('email')} type="email" autoComplete="username" required error={problemText('email', 'login.problem.email')} />
            <PasswordInput size={Size.Medium} label={t('login.password')} value={draft.password} onChange={set('password')} purpose="login" required error={problemText('password', 'login.problem.password')} />
            <p className={styles.forgot}>
              <button type="button" className={styles.link} onClick={() => { setMessage(null); setStage({ kind: 'forgot', sent: false }); }} data-login="forgot">
                {t('login.forgot')}
              </button>
              {' '}
              <button type="button" className={styles.link} onClick={() => { setMessage(null); setStage({ kind: 'code' }); }} data-login="haveCode">
                {t('login.haveCode')}
              </button>
            </p>
            {locked ? (
              <p className={styles.status} role="status" data-login-locked={String(secondsLeft)}>
                {t('login.locked')}
                {' '}
                {lockoutCountdown(secondsLeft)}
              </p>
            ) : null}
            <Button size={Size.Medium} color={Color.Navy} text={[t('login.signIn')]} actions={[() => undefined]} type="submit" disabled={busy || locked} />

            <hr className={styles.rule} />
            <p className={styles.footnote}>{t('login.noAccount')}</p>
          </form>
        ) : null}

        {stage.kind === 'factor' ? (
          <form className={styles.form} onSubmit={(event) => void verifyCode(event)} data-login-stage="factor" noValidate>
            <h1 id="login-title" className={styles.title}>{t('login.factorTitle')}</h1>
            <p className={styles.footnote}>{t('login.factorBody')}</p>
            {/* 🔨 The code is asked for after the password held, on its own step (decision 0325); a recovery code is typed in the same field. */}
            <TextInput size={Size.Medium} label={t('login.code')} value={draft.code} onChange={set('code')} type="text" autoComplete="one-time-code" placeholder={t('login.codePlaceholder')} />
            {/* Trust this device: the code verified now stands for the second factor here for the set number of days (decision 0324); withdrawn when the setting is 0. */}
            {stage.trustedDeviceDays > 0 ? (
              <span data-login-trust-device={String(stage.trustedDeviceDays)}>
                <Checkbox label={line(t('login.trustDevice'), stage.trustedDeviceDays, t('login.trustDays'))} checked={draft.trustDevice} onChange={set('trustDevice')} size={Size.Small} />
              </span>
            ) : null}
            <Button size={Size.Medium} color={Color.Navy} text={[t('login.verify')]} actions={[() => undefined]} type="submit" disabled={busy || !draft.code.trim()} />
            {/* Use a passkey stands beside the code where the login holds one; the password step before this one is unchanged (decision 0351). */}
            {stage.passkey ? (
              <span data-login-passkey="offered">
                <Button size={Size.Medium} color={Color.Inactive} text={[t('login.usePasskey')]} actions={[() => void signInWithPasskey()]} disabled={busy} />
              </span>
            ) : null}
            <p className={styles.forgot}>
              <button type="button" className={styles.link} onClick={() => { setMessage(null); setDraft((d) => ({ ...d, code: '' })); setStage({ kind: 'form' }); }} data-login="back">{t('login.back')}</button>
            </p>
          </form>
        ) : null}

        {stage.kind === 'enrol' ? (
          <form className={styles.form} onSubmit={(event) => void enrol(event)} data-login-stage="enrol" noValidate>
            <h1 id="login-title" className={styles.title}>{t('login.enrolTitle')}</h1>
            <p className={styles.footnote}>{t('login.enrolHint')}</p>
            {/* The secret as a QR code the authenticator scans, drawn in the browser as a data URL (product owner, 2026-09-12); the secret stays typed beneath it. */}
            {qrDataUrl ? <img className={styles.qr} src={qrDataUrl} alt={t('login.enrolScan')} data-login="qr" /> : null}
            <p className={styles.footnote}>{t('login.enrolScan')}</p>
            <p className={styles.secretLabel}>{t('login.enrolSecret')}</p>
            <code className={styles.secret} data-login="secret">{stage.secret}</code>
            <p className={styles.footnote}>
              {urlLine}
              <a className={styles.link} href={stage.otpauthUrl}>{stage.otpauthUrl}</a>
            </p>
            <TextInput size={Size.Medium} label={t('login.code')} value={enrolCode} onChange={setEnrolCode} type="text" autoComplete="one-time-code" required />
            <Button size={Size.Medium} color={Color.Navy} text={[t('login.enrolConfirm')]} actions={[() => undefined]} type="submit" disabled={busy} />
          </form>
        ) : null}

        {stage.kind === 'forgot' ? (
          <form className={styles.form} onSubmit={(event) => void requestReset(event)} data-login-stage="forgot" noValidate>
            <h1 id="login-title" className={styles.title}>{t('login.forgotTitle')}</h1>
            <p className={styles.footnote}>{stage.sent ? t('login.forgotSent') : t('login.forgotBody')}</p>
            {stage.sent ? null : <TextInput size={Size.Medium} label={t('login.email')} value={draft.email} onChange={set('email')} type="email" autoComplete="username" required />}
            {stage.sent ? null : <Button size={Size.Medium} color={Color.Navy} text={[t('login.forgotSend')]} actions={[() => undefined]} type="submit" disabled={busy || !draft.email.trim()} />}
            <p className={styles.forgot}>
              <button type="button" className={styles.link} onClick={() => setStage({ kind: 'form' })} data-login="back">{t('login.back')}</button>
            </p>
          </form>
        ) : null}

        {stage.kind === 'code' ? (
          <form className={styles.form} onSubmit={(event) => void redeemCode(event)} data-login-stage="code" noValidate>
            <h1 id="login-title" className={styles.title}>{t('login.codeTitle')}</h1>
            <p className={styles.footnote}>{t('login.codeBody')}</p>
            <TextInput size={Size.Medium} label={t('login.activationCode')} value={activationCode} onChange={setActivationCode} type="text" autoComplete="one-time-code" required />
            <Button size={Size.Medium} color={Color.Navy} text={[t('login.redeem')]} actions={[() => undefined]} type="submit" disabled={busy || !activationCode.trim()} />
            <p className={styles.forgot}>
              <button type="button" className={styles.link} onClick={() => setStage({ kind: 'form' })} data-login="back">{t('login.back')}</button>
            </p>
          </form>
        ) : null}

        {stage.kind === 'activate' ? (
          <form className={styles.form} onSubmit={(event) => void activate(event)} data-login-stage="activate" noValidate>
            <h1 id="login-title" className={styles.title}>{t('login.activateTitle')}</h1>
            <p className={styles.footnote}>{t('login.activateHint')}</p>
            <TextInput size={Size.Medium} label={t('login.email')} value={stage.email} onChange={() => undefined} type="email" disabled />
            <PasswordInput size={Size.Medium}
              label={t('login.newPassword')}
              value={newPassword}
              onChange={setNewPassword}
              purpose="set"
              required
              error={newPassword && passwordIssues.includes('default') ? t('login.isDefault') : newPassword && passwordIssues.includes('length') ? t('login.tooShort') : null}
            />
            <PasswordInput size={Size.Medium} label={t('login.newPasswordAgain')} value={newPasswordAgain} onChange={setNewPasswordAgain} purpose="set" required error={mismatch ? t('login.mismatch') : null} />
            <Button size={Size.Medium} color={Color.Navy} text={[t('login.activateSave')]} actions={[() => undefined]} type="submit" disabled={busy || passwordIssues.length > 0 || newPassword !== newPasswordAgain} />
          </form>
        ) : null}

        {stage.kind === 'recoveryCodes' ? (
          <div className={styles.form} data-login-stage="recoveryCodes">
            <h1 id="login-title" className={styles.title}>{t('login.recoveryTitle')}</h1>
            <p className={styles.footnote}>{t('login.recoveryHint')}</p>
            <ul className={styles.codes} data-login="recovery-codes">
              {stage.codes.map((code) => (
                <li key={code}><code className={styles.secret}>{code}</code></li>
              ))}
            </ul>
            <Button size={Size.Medium} color={Color.Navy} text={[t('login.recoveryContinue')]} actions={[() => navigate(destination)]} />
          </div>
        ) : null}

        {stage.kind === 'setPassword' ? (
          <form className={styles.form} onSubmit={(event) => void changePassword(event)} data-login-stage="setPassword" noValidate>
            <h1 id="login-title" className={styles.title}>{t('login.changeTitle')}</h1>
            <p className={styles.footnote}>{t('login.changeHint')}</p>
            <PasswordInput size={Size.Medium}
              label={t('login.newPassword')}
              value={newPassword}
              onChange={setNewPassword}
              purpose="change"
              required
              error={newPassword && passwordIssues.includes('default') ? t('login.isDefault') : newPassword && passwordIssues.includes('length') ? t('login.tooShort') : null}
            />
            <PasswordInput size={Size.Medium} label={t('login.newPasswordAgain')} value={newPasswordAgain} onChange={setNewPasswordAgain} purpose="change" required error={mismatch ? t('login.mismatch') : null} />
            <Button size={Size.Medium} color={Color.Navy} text={[t('login.changeSave')]} actions={[() => undefined]} type="submit" disabled={busy || passwordIssues.length > 0 || newPassword !== newPasswordAgain} />
          </form>
        ) : null}
      </section>
    </main>
  );
}
